/**
 * Zapier Ops Alerts tab.
 *
 * Manage notification channels (Slack / Teams / Email / raw Webhook)
 * that fan out on dead-letter deliveries, revoked integrations, CRM
 * sync failures, etc.
 *
 * Consumes:
 *   GET    /api/admin/zapier/notification-channels
 *   POST   /api/admin/zapier/notification-channels
 *   POST   /api/admin/zapier/notification-channels/{id}/toggle
 *   DELETE /api/admin/zapier/notification-channels/{id}
 *   POST   /api/admin/zapier/notification-channels/{id}/test
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell, Plus, Trash2, Send, RefreshCcw, Loader2, Slack, Mail,
  Webhook as WebhookIcon, MessageSquare, ShieldAlert,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const CHANNEL_ICONS = {
  slack: Slack,
  teams: MessageSquare,
  email: Mail,
  webhook: WebhookIcon,
};

const CHANNEL_LABELS = {
  slack: 'Slack Incoming Webhook',
  teams: 'Teams Incoming Webhook',
  email: 'Email Address',
  webhook: 'Raw Webhook URL',
};

const TARGET_PLACEHOLDER = {
  slack: 'https://hooks.slack.com/services/T…/B…/…',
  teams: 'https://outlook.office.com/webhook/…',
  email: 'ops@ievntur.com',
  webhook: 'https://your-service.example.com/hooks/zapier',
};

function ChannelCreateCard({ availableEvents, onCreated }) {
  const [name, setName] = useState('');
  const [channelType, setChannelType] = useState('slack');
  const [target, setTarget] = useState('');
  const [events, setEvents] = useState(['*']);
  const [busy, setBusy] = useState(false);

  const toggleEvent = (evt) => {
    setEvents((prev) => (prev.includes(evt) ? prev.filter((e) => e !== evt) : [...prev, evt]));
  };

  const submit = async () => {
    if (name.trim().length < 1) { toast.error('Name is required'); return; }
    if (target.trim().length < 3) { toast.error('Target is required'); return; }
    if (events.length === 0) { toast.error('Pick at least one event'); return; }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/zapier/notification-channels`,
        {
          name: name.trim(),
          channel_type: channelType,
          target: target.trim(),
          subscribed_events: events,
          enabled: true,
        },
        AUTH(),
      );
      toast.success('Channel created');
      setName(''); setTarget(''); setEvents(['*']);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5" data-testid="alerts-create-card">
      <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
        <Plus className="w-5 h-5 text-indigo-600" /> New Alert Channel
      </h3>
      <div className="space-y-3">
        <Input
          data-testid="alerts-new-name"
          placeholder="Channel name (e.g. #ops-zapier)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="flex flex-wrap gap-2">
          {Object.keys(CHANNEL_LABELS).map((t) => {
            const Icon = CHANNEL_ICONS[t];
            const active = channelType === t;
            return (
              <button
                key={t}
                onClick={() => setChannelType(t)}
                className={`px-3 py-1.5 rounded-full text-xs border flex items-center gap-1.5 transition ${
                  active
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'
                }`}
                data-testid={`alerts-type-${t}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t}
              </button>
            );
          })}
        </div>
        <Input
          data-testid="alerts-new-target"
          placeholder={TARGET_PLACEHOLDER[channelType]}
          value={target}
          onChange={(e) => setTarget(e.target.value)}
        />
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1.5">
            Subscribed events
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => toggleEvent('*')}
              className={`px-2 py-0.5 rounded-full text-xs border transition ${
                events.includes('*')
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'
              }`}
              data-testid="alerts-event-chip-*"
            >
              * (all)
            </button>
            {(availableEvents || []).map((evt) => (
              <button
                key={evt}
                onClick={() => toggleEvent(evt)}
                className={`px-2 py-0.5 rounded-full text-xs border transition ${
                  events.includes(evt)
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'
                }`}
                data-testid={`alerts-event-chip-${evt}`}
              >
                {evt}
              </button>
            ))}
          </div>
        </div>
        <Button
          data-testid="alerts-new-submit"
          disabled={busy}
          onClick={submit}
          className="w-full"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          Create Channel
        </Button>
      </div>
    </Card>
  );
}

function ChannelRow({ channel, onRefresh }) {
  const Icon = CHANNEL_ICONS[channel.channel_type] || WebhookIcon;
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    try {
      await axios.post(`${API}/admin/zapier/notification-channels/${channel.id}/toggle`, {}, AUTH());
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete channel "${channel.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/zapier/notification-channels/${channel.id}`, AUTH());
      toast.success('Channel deleted');
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const testFire = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/zapier/notification-channels/${channel.id}/test`,
        {},
        AUTH(),
      );
      if (data.ok) toast.success(`Test sent · ${data.response_code || 'ok'}`);
      else toast.error(`Test failed · ${data.error || data.response_code}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    finally { setBusy(false); }
  };

  return (
    <Card
      className="p-4 flex items-center justify-between gap-3 flex-wrap"
      data-testid={`alerts-channel-row-${channel.id}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <Icon className="w-5 h-5 text-slate-500 flex-shrink-0" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold truncate">{channel.name}</span>
            <Badge variant={channel.enabled ? 'default' : 'secondary'}>
              {channel.enabled ? 'enabled' : 'paused'}
            </Badge>
            <Badge variant="outline">{channel.channel_type}</Badge>
            {channel.failure_streak > 0 && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <ShieldAlert className="w-3 h-3" /> {channel.failure_streak} recent failure(s)
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1 truncate">
            {channel.target}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-1">
            {(channel.subscribed_events || []).map((e) => (
              <span key={e} className="bg-slate-100 rounded px-1.5 py-0.5">{e}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={testFire}
          disabled={busy}
          data-testid={`alerts-test-${channel.id}`}
        >
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
          Test
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={toggle}
          data-testid={`alerts-toggle-${channel.id}`}
        >
          {channel.enabled ? 'Pause' : 'Resume'}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={remove}
          data-testid={`alerts-delete-${channel.id}`}
        >
          <Trash2 className="w-4 h-4 text-rose-600" />
        </Button>
      </div>
    </Card>
  );
}

export default function AlertsTab() {
  const [channels, setChannels] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/zapier/notification-channels`, AUTH());
      setChannels(data.channels || []);
      setAvailableEvents(data.available_events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="alerts-tab">
      <Card className="p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Bell className="w-6 h-6 text-indigo-600" />
          <div>
            <h3 className="text-lg font-semibold">Ops Alerts</h3>
            <p className="text-sm text-slate-600 max-w-lg">
              Route dead-letter deliveries, revoked integrations, and CRM sync failures to
              Slack, Teams, email, or a raw webhook. Test any channel before you rely on it.
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="alerts-refresh">
          <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </Card>

      <ChannelCreateCard availableEvents={availableEvents} onCreated={load} />

      <div>
        <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Configured Channels
          <span className="ml-2 text-xs font-normal text-slate-500 normal-case">
            {channels.length} total
          </span>
        </h4>
        {loading && (
          <div className="text-center py-6 text-slate-500" data-testid="alerts-loading">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading channels…
          </div>
        )}
        {!loading && channels.length === 0 && (
          <Card className="p-8 text-center text-slate-500" data-testid="alerts-empty">
            <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No alert channels yet. Add one above to route ops alerts to your team.
          </Card>
        )}
        <div className="space-y-2">
          {channels.map((c) => (
            <ChannelRow key={c.id} channel={c} onRefresh={load} />
          ))}
        </div>
      </div>
    </div>
  );
}
